import { makeObservable, observable } from 'mobx';
import type { Aggregate } from 'edit/Aggregate';
import type { AutoSubmitMode, WaitEvent } from 'api/node_definitions/endpoints/types';
import type { EditableNodeDefinition } from './EditableNodeDefinition';

/**
 * The auto-submit dropdown collapses the two wire fields (`auto_submit` + `auto_submit_mode`) into one of three states.
 * `'off'` means don't auto-submit and carries no mode — the backend rejects a mode without `auto_submit`.
 */
export type AutoSubmitSelection = 'off' | AutoSubmitMode;

// Monotonic per-instance key for React list rendering. Wait events have no stable server id, so each instance gets a unique
// key at construction that never changes across add/remove.
let nextWaitEventKey = 0;

/**
 * One definition-level wait-event link, as a reactive child of an `EditableNodeDefinition`. The parent→child relationship is
 * owned by a shared `Aggregate` that both sides hold; this child routes its self-delete through it.
 *
 * `path` and `schedulingWindow` are `"zone|name"` strings the backend resolves. `autoSubmitSelection` is the single field
 * behind the auto-submit dropdown; `toSave` expands it back to the two wire fields. The "auto-submit needs a default queue"
 * rule is surfaced as an informational note on the tab, not enforced here or in the UI — the server is the validation
 * authority, so the model just stores and round-trips what's chosen.
 */
export class EditableWaitEvent {
  private readonly _aggregate: Aggregate<EditableNodeDefinition, EditableWaitEvent>;
  autoClear = true;
  autoSubmitSelection: AutoSubmitSelection = 'off';
  /** IANA override for the auto-submit; null uses the configured default. Kept while auto-submit is off, but not saved then. */
  autoSubmitTimezone: string | null = null;
  /** Stable identity for React list keys; never changes after construction. */
  readonly key = `wait-event-${(nextWaitEventKey += 1)}`;
  path: string | null = null;
  precondition: string | null = null;
  schedulingWindow: string | null = null;

  /**
   * @param aggregate the shared parent→children owner; self-delete routes through it.
   * @param wire when present, seeds from a loaded wait event; when absent, a fresh event that defaults to auto-submitting
   *   (`if_none_pending`) only when the definition already has a default queue to submit into — matching the old add behaviour.
   */
  constructor(aggregate: Aggregate<EditableNodeDefinition, EditableWaitEvent>, wire?: WaitEvent) {
    this._aggregate = aggregate;
    makeObservable(this, {
      autoClear: observable,
      autoSubmitSelection: observable,
      autoSubmitTimezone: observable,
      path: observable,
      precondition: observable,
      schedulingWindow: observable,
    });
    if (wire !== undefined) {
      this.path = wire.path;
      this.autoClear = wire.auto_clear ?? true;
      this.autoSubmitSelection = wire.auto_submit ? (wire.auto_submit_mode ?? 'if_none_pending') : 'off';
      this.autoSubmitTimezone = wire.auto_submit_timezone ?? null;
      this.schedulingWindow = wire.scheduling_window ?? null;
      this.precondition = wire.precondition ?? null;
    } else {
      this.autoSubmitSelection = aggregate.parent.defaultQueuePath ? 'if_none_pending' : 'off';
    }
  }

  /** A one-line summary for the card subtitle, or `null` before an event is chosen. */
  get summary(): string | null {
    if (!this.path) return null;
    const name = this.path.split('|')[1] ?? this.path;
    const submit = this.autoSubmitSelection === 'off' ? 'no auto-submit' : this.autoSubmitSelection === 'always' ? 'always submit' : 'submit if none pending';
    const submitIn = this.autoSubmitSelection !== 'off' && this.autoSubmitTimezone !== null ? `${submit} in ${this.autoSubmitTimezone}` : submit;
    return `${name} · ${submitIn} · ${this.autoClear ? 'auto-clear' : 'manual clear'}`;
  }

  /** Remove this wait event from its definition. */
  delete = (): void => {
    this._aggregate.remove(this);
  };

  /**
   * Project to the wire shape. `'off'` clears the mode and the timezone, since the backend rejects either without `auto_submit`;
   * any submit selection sets `auto_submit` and carries both.
   */
  toSave(): WaitEvent {
    const selection = this.autoSubmitSelection;
    return {
      path: this.path,
      auto_submit: selection !== 'off',
      auto_submit_mode: selection === 'off' ? null : selection,
      auto_submit_timezone: selection === 'off' ? null : this.autoSubmitTimezone,
      auto_clear: this.autoClear,
      scheduling_window: this.schedulingWindow,
      precondition: this.precondition,
    };
  }
}
